import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  ROUTE_DATE_RELATION_LABELS,
  formatRouteDate,
  routeDateRelation,
  shiftDate,
} from "../lib/normalize";

/**
 * 配送ルートの表示日を切り替えるストリップ (#768)。
 * 「‹ 8/17 (月) ›」の前後日ボタンに加え、ラベルを覆う日付入力から任意の日へ飛べる
 * (62 日前まで 1 日ずつ戻るのは現実的でないため)。
 *
 * 今日以外は閲覧のみ。ドライバーが「ボタンが出ない = 壊れている」と誤解しないよう、
 * バッジと注記でその日がどの日で何ができないかを明示する。
 */
export function DateStrip({
  value,
  min,
  max,
  onChange,
}: {
  /** 表示中の配送日 "YYYY-MM-DD" */
  value: string;
  /** 選べる最古の配送日 "YYYY-MM-DD" */
  min: string;
  /** 選べる最新の配送日 "YYYY-MM-DD" */
  max: string;
  onChange: (date: string) => void;
}) {
  const relation = routeDateRelation(value);

  return (
    <div className="border-b border-[var(--rule-soft)] bg-[var(--bg-raised)]">
      <div className="flex items-center justify-between gap-2 px-3 py-2">
        <button
          type="button"
          aria-label="前の日"
          disabled={value <= min}
          onClick={() => onChange(shiftDate(value, -1))}
          className="flex h-11 w-11 items-center justify-center rounded-lg text-[var(--ink-2)] hover:bg-[var(--bg-sunken)] disabled:cursor-not-allowed disabled:opacity-30"
        >
          <ChevronLeft size={20} />
        </button>

        <div className="relative flex min-h-11 flex-1 items-center justify-center gap-1.5 rounded-lg">
          <span className="num text-[length:var(--fs-md)] font-semibold">
            {formatRouteDate(value)}
          </span>
          <span
            className={
              relation === "today"
                ? "rounded-full bg-[var(--st-done-bg)] px-2 py-0.5 text-[length:var(--fs-xs)] font-semibold text-[var(--st-done-ink)]"
                : "rounded-full bg-[var(--st-assigned-bg)] px-2 py-0.5 text-[length:var(--fs-xs)] font-semibold text-[var(--st-assigned-ink)]"
            }
          >
            {ROUTE_DATE_RELATION_LABELS[relation]}
          </span>
          {/*
            ラベルの上に透明な日付入力を重ね、タップでそのまま OS の日付ピッカーを開く。
            入力を隠すと (display:none / sr-only) LIFF の WebView でピッカーが開かないため、
            見た目だけ透明にして当たり判定は残す。
          */}
          <input
            type="date"
            aria-label="表示する配送日"
            value={value}
            min={min}
            max={max}
            onChange={(e) => {
              // ピッカーのクリアや未確定入力で空文字が来る。選択日を失わないよう無視する。
              if (e.target.value) onChange(e.target.value);
            }}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          />
        </div>

        <button
          type="button"
          aria-label="次の日"
          disabled={value >= max}
          onClick={() => onChange(shiftDate(value, 1))}
          className="flex h-11 w-11 items-center justify-center rounded-lg text-[var(--ink-2)] hover:bg-[var(--bg-sunken)] disabled:cursor-not-allowed disabled:opacity-30"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {relation !== "today" && (
        <div className="px-3 pb-2 text-center text-[length:var(--fs-xs)] text-[var(--ink-3)]">
          閲覧のみ（打刻・記録はできません）
        </div>
      )}
    </div>
  );
}
